// IAP store service. Preview adapter on web; RevenueCat on native once
// public SDK keys replace "Replace later" and the Purchases plugin is synced.

import {
    getHeartRefillProductId,
    getIapProductById,
    getNoAdsProductId,
    listIapProducts,
} from "./iapCatalog";
import { loadIapEntitlements } from "./iapEntitlements";
import {
    getNativePlatform,
    isNativeApp,
    isNativeBillingReady,
} from "./nativePlatform";
import {
    buildCatalogGrant,
    customerInfoHasNoAds,
    fetchNativeStoreProducts,
    identifyRevenueCatUser,
    purchaseNativeStoreProduct,
    resetRevenueCatUser,
    restoreNativePurchases,
} from "./revenueCatBilling";

export const IAP_STORE_STUB_DELAY_MS = 650;

export const IAP_PLATFORMS = {
    PREVIEW: "preview",
    WEB: "web",
    IOS: "ios",
    ANDROID: "android",
};

function delay(ms) {
    return new Promise((resolve) => setTimeout(resolve, ms));
}

function resolveIapPlatform() {
    if (!isNativeApp()) {
        return IAP_PLATFORMS.PREVIEW;
    }

    const native = getNativePlatform();
    if (native === IAP_PLATFORMS.IOS) {
        return IAP_PLATFORMS.IOS;
    }
    if (native === IAP_PLATFORMS.ANDROID) {
        return IAP_PLATFORMS.ANDROID;
    }
    return IAP_PLATFORMS.PREVIEW;
}

/**
 * Detect runtime store capability. Capacitor shell may be present while
 * billing still uses the preview adapter until RevenueCat is wired.
 */
export function getIapStoreStatus() {
    const nativeShell = isNativeApp();
    const billingReady = isNativeBillingReady();
    const platform = resolveIapPlatform();
    const isPreview = !billingReady;

    if (nativeShell && !billingReady) {
        return {
            platform,
            ready: true,
            isPreview: true,
            nativeAvailable: true,
            nativeShell: true,
            billingReady: false,
            message:
                "Preview store — no real charge until billing is live.",
        };
    }

    if (billingReady) {
        return {
            platform,
            ready: true,
            isPreview: false,
            nativeAvailable: true,
            nativeShell: true,
            billingReady: true,
            message: "",
        };
    }

    return {
        platform: IAP_PLATFORMS.PREVIEW,
        ready: true,
        isPreview,
        nativeAvailable: false,
        nativeShell: false,
        billingReady: false,
        message:
            "Preview store — no real charge until billing is live.",
    };
}

export function listStoreProducts(options = {}, nativeProducts = []) {
    const status = getIapStoreStatus();
    const nativeById = (nativeProducts || []).reduce((map, item) => {
        if (item && item.identifier) {
            map[item.identifier] = item;
        }
        return map;
    }, {});

    return listIapProducts(options).map((product) => {
        const native = nativeById[product.sku];
        const livePrice =
            native && (native.priceString || native.price_string);
        return {
            ...product,
            priceLabel: livePrice || product.previewPriceLabel,
            isPreview: status.isPreview,
        };
    });
}

export async function fetchStoreProducts(options = {}) {
    if (isNativeBillingReady()) {
        try {
            const nativeProducts = await fetchNativeStoreProducts();
            return listStoreProducts(options, nativeProducts);
        } catch (_) {
            return listStoreProducts(options);
        }
    }

    await delay(120);
    return listStoreProducts(options);
}

/**
 * Purchase a catalog product. Native uses RevenueCat; web uses preview grants.
 * @returns {Promise<{ productId: string, granted: true, grant: object, isPreview: boolean }>}
 */
export async function purchaseStoreProduct(productId) {
    const product = getIapProductById(productId);

    if (!product || product.enabled === false) {
        const error = new Error("Unknown or disabled store product.");
        error.code = "iap-unknown-product";
        throw error;
    }

    if (isNativeBillingReady()) {
        return purchaseNativeStoreProduct(productId);
    }

    await delay(IAP_STORE_STUB_DELAY_MS);

    const status = getIapStoreStatus();

    return {
        productId: product.id,
        sku: product.sku,
        granted: true,
        isPreview: status.isPreview,
        grant: buildCatalogGrant(product),
    };
}

/**
 * Restore non-consumables. Heart packs are consumable (not restored).
 * Native restore reads RevenueCat entitlements; preview uses local No Ads.
 */
export async function restoreStorePurchases() {
    if (isNativeBillingReady()) {
        return restoreNativePurchases();
    }

    await delay(200);
    const entitlements = loadIapEntitlements();
    const restored = [];

    if (entitlements.noAds) {
        const product = getIapProductById(getNoAdsProductId());
        restored.push({
            productId: getNoAdsProductId(),
            sku: product?.sku || getNoAdsProductId(),
            grant: buildCatalogGrant(product, { removeAds: true }),
        });
    }

    return {
        restored,
        isPreview: getIapStoreStatus().isPreview,
    };
}

/**
 * Attach the signed-in Firebase uid to RevenueCat (no-op on preview/web).
 */
export async function syncNativeBillingUser(appUserID = null) {
    if (!isNativeBillingReady()) {
        return { synced: false, noAds: false };
    }

    if (!appUserID) {
        await resetRevenueCatUser();
        return { synced: true, noAds: false };
    }

    const customerInfo = await identifyRevenueCatUser(appUserID);
    return {
        synced: true,
        noAds: customerInfoHasNoAds(customerInfo),
        customerInfo,
    };
}

/**
 * Convenience used by the out-of-hearts funnel "Refill Hearts" button.
 */
export async function purchaseHeartRefillProduct(options = {}) {
    const productId =
        options.productId ||
        (options.pack === "single"
            ? "hearts_1"
            : getHeartRefillProductId());

    return purchaseStoreProduct(productId);
}
